// Types and adapter for the Clinical Review result value (design: Clinical Assessment).
//
// The ClinicalReview action produces a per-building `clinical_review_result` — the AI-derived
// admission criteria matched against that building's packet. Its content IS the criteria array
// (no wrapper object), so the value follows the same family convention as `payer_candidates` and
// `attachments_processed`: the produced value is its list. It is a committed, group-stamped
// workflow value: the result is committed before the coordinator reviews it, so we read it off the
// workflow (via `valueFor`, group-scoped to the building's action) rather than off the action's
// uncommitted `outputs` — the same source the other fanout result renderers read from
// (`CompletedResult`, `FanoutPanel`). This module owns that read and narrows it to a typed shape,
// mirroring how `eligibilityData.ts` owns the PayerSearch/BenefitsEligibility payloads.
//
// Value NAMES are snake_case and read verbatim (olio-api leaves them alone); the value CONTENT is
// camelCased by olio-api's OliveBranch, so every field below is camelCase.

import { matchStrengthRank } from 'components/shared/ai/AdmissionsCriteria';
import isRecord from 'lib/isRecord';
import { CitationHighlight, parseCitationHighlights, parseCitationPages } from 'models/CitationHighlight';
import Workflow, { WorkflowAction } from 'models/Workflow';

// The `clinical_review_result` value written by the ClinicalReview action, group-stamped per building.
export const CLINICAL_REVIEW_RESULT_VALUE = 'clinical_review_result';

// One matched admission criteria in a clinical review result. The value carries ONLY the criteria the
// AI matched: the api filters at publish time, because nothing renders the negatives — those stay on
// the api's `ai_job_criteria` rows, where the AIJob export reads them. So there is no matched-ness to
// test on this shape.
// Named `…Criteria` to follow the API's model naming (ai_criteria / ai_job_criteria), not the FE's
// older `…Criteria`. Keys arrive camelCase — the API response is camelCased by OliveBranch.
// `criteria` (the matched text) mirrors the API column and follows it if/when that column is renamed
// to `label`. `order` places this criteria within its category, and `category.order` places the
// category among its siblings — both drive display order (see `orderClinicalReviewCriteria`) rather
// than relying on the payload's array position.
//
// `category.id` is optional: the api publishes it from the frozen category snapshot on the
// ai_job_criteria, and that snapshot only carries an id once OLIO-2697's api change is deployed
// (there is deliberately no backfill, so older rows never gain one). It arrives null until then,
// which is why every consumer keys off `category.id ?? category.name`.
export interface ClinicalReviewCriteria {
  aiCriteriaId: string;
  label: string;
  description: string;
  order: number;
  category: { id?: string; name: string; color: string; order: number } | null;
  // How strongly the criteria is evidenced ('strong' | 'likely' | 'possible'), or null on a criteria
  // that predates the enrichment. Presence is what gates the reviewer card's disclosure.
  matchStrength: string | null;
  finding: string | null;
  // `pages` are the pages of the cited document the AI found the evidence on. Absent on a criteria
  // evaluated before olio-api began recording them, and empty for a citation it could not place on
  // a page — so every reader treats it as optional.
  //
  // De-duplicated and ASCENDING, and that ordering is olio-api's guarantee, not an accident of the
  // payload: `AIJob::CriteriaEvaluation#cited_attachments` sorts them, and folds in every page a highlight
  // sits on. The summary surface feeds the same prop in citation order instead, so the two callers
  // disagree on what the first entry means, on purpose.
  // `highlights` are the cited blocks' outlines, in reading order; the document viewer opens on the
  // first and steps through the rest (see CitationDocumentViewer).
  sources: Array<{ attachmentId: string; displayId: number; pages?: number[]; highlights?: CitationHighlight[] }>;
}

// Narrow a raw `clinical_review_result` payload — the building's matched criteria — to the typed
// shape. The payload is structured (unlike the scalar-value common case), so read it as unknown
// and validate at runtime — returns [] for a missing payload or any non-array shape, mirroring
// `parsePayerCandidates`.
export function parseClinicalReviewResult(value: unknown): ClinicalReviewCriteria[] {
  if (!Array.isArray(value)) return [];
  // Each source's pages and outlines are re-checked rather than cast, and entries that are not objects
  // skipped: a stored value is only as good as whatever wrote it, and a malformed box would draw at NaN
  // or scroll nowhere.
  return (value as ClinicalReviewCriteria[]).filter(isRecord).map((criteria) => ({
    ...criteria,
    sources: Array.isArray(criteria.sources)
      ? criteria.sources.filter(isRecord).map((source) => ({
          ...source,
          pages: parseCitationPages(source.pages),
          highlights: parseCitationHighlights(source.highlights),
        }))
      : [],
  }));
}

// Sort matched criteria into display order: by category `order`, then by match strength within the
// category (strong, likely, possible — see `matchStrengthRank`), then by each criteria's own `order`.
// Strength outranks the configured criteria order because the reviewer reads a category top-down and
// wants the best-evidenced matches first; the configured order still breaks ties within a tier, and
// still fully determines the order of a legacy result where nothing carries a strength.
// Uncategorized criteria (null category) sort last. The sort is stable, so fully equal keys keep
// their payload order.
export function orderClinicalReviewCriteria(criteria: ClinicalReviewCriteria[]): ClinicalReviewCriteria[] {
  const categoryRank = (entry: ClinicalReviewCriteria) => (entry.category ? (entry.category.order ?? 0) : Infinity);
  return [...criteria].sort(
    (a, b) =>
      categoryRank(a) - categoryRank(b) ||
      matchStrengthRank(a.matchStrength) - matchStrengthRank(b.matchStrength) ||
      (a.order ?? 0) - (b.order ?? 0)
  );
}

// The matched criteria for one building's ClinicalReview action, read off the committed workflow
// value for that building. `clinical_review_result` is group-stamped, so resolve it through
// `valueFor` scoped to the action's `groupId` (each building has its own ClinicalReview action and
// its own group-scoped value). The value's content is the criteria array; read it `as unknown`
// because `WorkflowValue.value` is typed for scalars while this kind stores a structured payload
// (the same cast `CompletedResult` uses for its nested-object results).
export function readClinicalReviewResult(workflow: Workflow, action: WorkflowAction): ClinicalReviewCriteria[] {
  const value = workflow.valueFor(CLINICAL_REVIEW_RESULT_VALUE, action)?.value as unknown;
  return orderClinicalReviewCriteria(parseClinicalReviewResult(value));
}

// The reviewer's dismissals, recorded on the action's own `outputs` by save_progress — never a
// committed value. Only dismissed criteria have an entry; an undismiss drops it. Who and when are
// stamped by the API from the request's user, so the payload this client sends carries neither.
export const CLINICAL_REVIEW_REVIEWS_VALUE = 'clinical_review_reviews';
export const DISMISS = 'dismiss';

export interface ClinicalReviewDismissal {
  aiCriteriaId: string;
  dismissedByName: string | null;
  dismissedAt: string | null;
}

export function readClinicalReviewDismissals(action: WorkflowAction): Map<string, ClinicalReviewDismissal> {
  const value = action.outputFor(CLINICAL_REVIEW_REVIEWS_VALUE);
  const entries = Array.isArray(value) ? (value as Array<Record<string, unknown> | null>) : [];
  return new Map(
    entries
      .filter((entry) => entry?.decision === DISMISS && typeof entry.aiCriteriaId === 'string')
      .map((entry) => [
        entry!.aiCriteriaId as string,
        {
          aiCriteriaId: entry!.aiCriteriaId as string,
          dismissedByName: (entry!.dismissedByName as string) ?? null,
          dismissedAt: (entry!.dismissedAt as string) ?? null,
        },
      ])
  );
}

// The save_progress payload that moves one criterion on `action`: the whole reviews array, as the
// API expects, with every other dismissal resent untouched so it keeps its stamp.
export function reviewsWith(action: WorkflowAction, aiCriteriaId: string, dismissed: boolean) {
  const others = [...readClinicalReviewDismissals(action).keys()].filter((id) => id !== aiCriteriaId);
  const ids = dismissed ? [...others, aiCriteriaId] : others;
  return { [CLINICAL_REVIEW_REVIEWS_VALUE]: ids.map((id) => ({ aiCriteriaId: id, decision: DISMISS })) };
}
